import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { DollarSign, TrendingUp, Award, Sparkles, Info, ChevronRight, Lock, Leaf } from 'lucide-react';
import axios from 'axios';
import { PriceSightingsPanel } from '@/components/PriceSightings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function GardenValue({ user }) {
  const [data, setData] = useState(null);
  const [proj, setProj] = useState(null);
  const [leaderboards, setLeaderboards] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPremium = user?.subscription_active && (user?.subscription_tier === 'premium' || user?.subscription_tier === 'pro');

  useEffect(() => {
    const load = async () => {
      try {
        const [d, p, lb] = await Promise.all([
          axios.get(`${API}/value/dashboard`, { withCredentials: true }),
          axios.get(`${API}/value/projection`, { withCredentials: true }),
          axios.get(`${API}/value/leaderboards/personal`, { withCredentials: true })
        ]);
        setData(d.data);
        setProj(p.data);
        setLeaderboards(lb.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <Layout user={user}><div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div></div></Layout>;
  if (!data) return <Layout user={user}><p className="p-6 text-[#7A6B57]">Couldn&apos;t load garden value.</p></Layout>;

  const fmt = (v) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const range = (t) => `${fmt(t.low)}–${fmt(t.high)}`;
  const totals = data.totals;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Hero */}
        <div>
          <p className="text-sm font-medium text-[#7A6B57] uppercase tracking-wider">Garden Value</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Look what your garden gave you
          </h1>
          <p className="text-base text-[#7A6B57] mt-2 max-w-2xl">
            Real-world retail value of what you grow, based on local produce prices. Estimates use ranges — not fake precision.
          </p>
        </div>

        {/* Top summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="value-summary">
          {[
            { key: 'week', label: 'This week' },
            { key: 'month', label: 'This month' },
            { key: 'season', label: 'This season' },
            { key: 'lifetime', label: 'Lifetime' },
          ].map(card => (
            <div key={card.key} className="bg-[#FAF6EC] rounded-2xl p-5 border border-[#1A4331]/10" data-testid={`value-${card.key}`}>
              <p className="text-xs font-bold tracking-wider uppercase text-[#7A6B57]">{card.label}</p>
              <p className="text-2xl md:text-3xl font-bold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {range(totals[card.key])}
              </p>
              <p className="text-xs text-[#7A6B57] mt-1">grown</p>
            </div>
          ))}
        </div>

        {/* Annual projection */}
        <div className="bg-gradient-to-br from-[#1A4331] to-[#255C44] rounded-3xl p-8 text-[#FAF6EC] relative overflow-hidden" data-testid="annual-projection">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#C9A85B]/10 rounded-full blur-3xl"></div>
          <div className="relative flex items-start justify-between flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-[#C9A85B]" />
                <p className="text-xs font-bold tracking-[0.2em] uppercase opacity-80">Estimated annual garden value</p>
              </div>
              <p className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {range(data.annual_projection)}
              </p>
              <p className="text-sm opacity-90 mt-3 max-w-md leading-relaxed">
                {proj?.ai_insight || `Confidence: ${data.annual_projection.confidence}. This estimate sharpens as you log more harvests across seasons.`}
              </p>
            </div>
            <div className="text-right text-xs opacity-70">
              <p>Source</p>
              <p className="font-bold uppercase text-sm tracking-wider text-[#C9A85B]">{data.price_source.replace('_', ' ')}</p>
              <p className="mt-2">Confidence</p>
              <p className="font-bold uppercase text-sm tracking-wider">{data.annual_projection.confidence}</p>
            </div>
          </div>
        </div>

        {/* Personal leaderboards */}
        <div className="grid md:grid-cols-3 gap-3">
          {/* Best by value */}
          <div className="bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10" data-testid="best-by-value">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-5 h-5 text-[#C9A85B]" />
              <h3 className="font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Best crops by value</h3>
            </div>
            {leaderboards?.best_by_value?.length === 0 ? (
              <p className="text-sm text-[#7A6B57] italic">Log harvests to see your top crops.</p>
            ) : (
              <ol className="space-y-2">
                {leaderboards?.best_by_value?.map((c, i) => (
                  <li key={c.crop_name} className="flex items-center gap-3 text-sm">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-[#C9A85B] text-white' : 'bg-[#E5EBDD] text-[#7A6B57]'}`}>{i + 1}</span>
                    <span className="flex-1 font-semibold text-[#1A4331]">{c.crop_name}</span>
                    <span className="text-[#C9A85B] font-bold">${c.value_low}–${c.value_high}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Best per m² */}
          <div className="bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10" data-testid="best-per-m2">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-[#6B9F4A]" />
              <h3 className="font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Best value per m²</h3>
            </div>
            {!isPremium ? (
              <div className="flex items-start gap-2 text-sm text-[#7A6B57]">
                <Lock className="w-4 h-4 text-[#C9A85B] flex-shrink-0 mt-0.5" />
                <p>Small-space stars deserve a fair leaderboard. Premium unlocks value-per-m² calculations. <a href="/subscription" className="text-[#C9A85B] font-semibold">Upgrade →</a></p>
              </div>
            ) : leaderboards?.best_per_m2?.length === 0 ? (
              <p className="text-sm text-[#7A6B57] italic">Connect plantings to beds for sharper estimates.</p>
            ) : (
              <ol className="space-y-2">
                {leaderboards?.best_per_m2?.map((c, i) => (
                  <li key={c.crop_name} className="flex items-center gap-3 text-sm">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-[#6B9F4A] text-white' : 'bg-[#E5EBDD] text-[#7A6B57]'}`}>{i + 1}</span>
                    <span className="flex-1 font-semibold text-[#1A4331]">{c.crop_name}</span>
                    <span className="text-[#6B9F4A] font-bold">${c.value_per_m2_low}–${c.value_per_m2_high}/m²</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Most reliable */}
          <div className="bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10" data-testid="most-reliable">
            <div className="flex items-center gap-2 mb-3">
              <Leaf className="w-5 h-5 text-[#1A4331]" />
              <h3 className="font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Most reliable</h3>
            </div>
            {leaderboards?.most_reliable?.length === 0 ? (
              <p className="text-sm text-[#7A6B57] italic">Track repeat harvests to identify your most dependable crops.</p>
            ) : (
              <ol className="space-y-2">
                {leaderboards?.most_reliable?.map((c, i) => (
                  <li key={c.crop_name} className="flex items-center gap-3 text-sm">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-[#1A4331] text-white' : 'bg-[#E5EBDD] text-[#7A6B57]'}`}>{i + 1}</span>
                    <span className="flex-1 font-semibold text-[#1A4331]">{c.crop_name}</span>
                    <span className="text-[#7A6B57] text-xs">{c.harvest_count}× harvested</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* Community price sightings + seed pack rewards */}
        <PriceSightingsPanel user={user} />

        {/* Confidence / Trust panel */}
        <div className="bg-[#FAF6EC]/60 rounded-3xl p-5 border border-[#1A4331]/8" data-testid="trust-panel">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[#7A6B57] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-[#7A6B57] leading-relaxed">
              <p className="font-bold tracking-wider uppercase text-[#1A4331] mb-2">How we estimate value</p>
              <p>Estimates use {data.country} produce price ranges from your selected source ({data.price_source.replace('_', ' ')}), updated regularly. Confidence: <strong>{data.confidence}</strong>.</p>
              <p className="mt-2">Why ranges, not exact figures? Prices vary by store, season, pack size, quality, and specials. We use ranges — never fake precision.</p>
              <p className="mt-2 italic">{data.confidence_reason}</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default GardenValue;
